import React, { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Gift, Loader2, CheckCircle2 } from "lucide-react";

const TIER_OPTIONS = [
 { value: "copilot", label: "Shield" },
 { value: "vault", label: "Vault" },
 { value: "dispute", label: "Dispute Mode" },
 { value: "pro", label: "Pro" },
];

/**
 * AdminGrantTierWidget. founder utility for the "founding 10" testimonial
 * offer in tenant Facebook groups. Grants any registered email a paid tier
 * for N days; an hourly background task auto-downgrades them after expiry.
 *
 * Hides itself for non-admins (the server returns 403 on /admin/grants which
 * is the gating signal. same pattern as AdminSpendWidget).
 */
export default function AdminGrantTierWidget() {
 const [allowed, setAllowed] = useState(null); // null = checking, false = hide, true = render
 const [email, setEmail] = useState("");
 const [tier, setTier] = useState("copilot");
 const [days, setDays] = useState(90);
 const [busy, setBusy] = useState(false);
 const [grants, setGrants] = useState([]);

 // Probe an admin-only endpoint to decide whether to render.
 const loadGrants = async () => {
 api.get("/admin/grants")
.then(({ data }) => { setGrants(data.grants || []); setAllowed(true); })
.catch(e => {
 if (e.response?.status === 403) {
 setAllowed(false);
 } else {
 // Non-403 means something else is broken (network/500). Log so admins
 // can spot transient issues; hide the widget either way (safer than
 // breaking the page for a real admin behind a flaky network).
 console.warn("AdminGrantTierWidget probe failed:", e);
 setAllowed(false);
 }
 });
 };

 useEffect(() => { loadGrants(); }, []);

 if (!allowed) return null;

 const submit = async (e) => {
 e.preventDefault();
 if (!email || !email.includes("@")) {
 toast.error("Enter a valid email. the user must have already registered.");
 return;
 }
 setBusy(true);
 try {
 const { data } = await api.post("/admin/grant-tier", { email: email.trim().toLowerCase(), tier, days });
 toast.success(`${TIER_OPTIONS.find(t => t.value === tier)?.label} granted to ${data.user_email}`, {
 description: `Expires ${new Date(data.expires_at).toLocaleDateString()}`,
 duration: 6000,
 });
 setEmail("");
 loadGrants();
 } catch (err) {
 toast.error(err.response?.data?.detail || "Couldn't grant tier. check the email is registered.");
 } finally {
 setBusy(false);
 }
 };

 return (
 <div className="card mb-6 border-l-4 border-l-[#1B6C62]" data-testid="admin-grant-tier-widget">
 <div className="flex items-center gap-2 mb-1">
 <Gift size={16} strokeWidth={1.5} className="text-[#1B6C62]" />
 <h3 className="font-serif text-xl text-[#0D2B52]">Grant a tier</h3>
 <span className="label-mono text-[10px] text-[#1B6C62]">Admin only · for the founding-10 offer</span>
 </div>
 <p className="text-xs text-[#6B7280] mb-4 italic">
 Use this for the Facebook-group testimonial cohort. The user must register first, then DM you their email. After expiry they auto-downgrade to Free.
 </p>

 <form onSubmit={submit} className="grid sm:grid-cols-[2fr_1fr_80px_auto] gap-2 mb-5 items-end">
 <div>
 <label className="label-mono mb-1 block">Email</label>
 <input
 type="email"
 value={email}
 onChange={e => setEmail(e.target.value)}
 placeholder="user@example.com"
 className="w-full border border-[#E5E4DE] px-3 py-2 text-sm focus:outline-none focus:border-[#1B6C62]"
 data-testid="grant-tier-email"
 required
 />
 </div>
 <div>
 <label className="label-mono mb-1 block">Tier</label>
 <select
 value={tier}
 onChange={e => setTier(e.target.value)}
 className="w-full border border-[#E5E4DE] px-3 py-2 text-sm focus:outline-none focus:border-[#1B6C62]"
 data-testid="grant-tier-tier"
 >
 {TIER_OPTIONS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
 </select>
 </div>
 <div>
 <label className="label-mono mb-1 block">Days</label>
 <input
 type="number"
 min="1"
 max="365"
 value={days}
 onChange={e => setDays(parseInt(e.target.value) || 90)}
 className="w-full border border-[#E5E4DE] px-3 py-2 text-sm focus:outline-none focus:border-[#1B6C62]"
 data-testid="grant-tier-days"
 />
 </div>
 <button
 type="submit"
 disabled={busy}
 className="btn-primary text-sm whitespace-nowrap inline-flex items-center gap-1.5"
 data-testid="grant-tier-submit"
 >
 {busy ? <Loader2 size={14} strokeWidth={1.5} className="animate-spin" /> : <CheckCircle2 size={14} strokeWidth={1.5} />}
 Grant
 </button>
 </form>

 {grants.length > 0 && (
 <div>
 <div className="label-mono mb-2">Recent grants</div>
 <div className="space-y-1 max-h-48 overflow-y-auto pr-2">
 {grants.slice(0, 20).map((g, i) => (
 <div key={i} className="flex justify-between text-xs border-b border-[#E5E4DE] py-1.5 font-mono" data-testid={`grant-row-${i}`}>
 <span className="truncate flex-1 text-[#1A1A1A]">{g.granted_to_email}</span>
 <span className="text-[#1B6C62] mx-2">{g.tier}</span>
 <span className="text-[#6B7280]">{g.days}d · until {new Date(g.expires_at).toLocaleDateString()}</span>
 </div>
 ))}
 </div>
 </div>
 )}
 </div>
 );
}
